import React, { useEffect, useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import { Menu } from '@ui/menus/Menu';
import { Icon } from '@ui/Icon';
import { SelectTrigger } from '@ui/SelectTrigger';
import { PhasesCard } from '@features/proposals/ScopeSelector/PhasesCard';
import { ResultCard } from '@features/proposals/ScopeSelector/ResultCard';
import { ProjectListFilter } from '@client/pages/MainView/components/AppBar/components/ProjectListFilter';
import {
  useProjectOwnerFilter,
  useProjectProgramFilter,
} from '@client/hooks/use-filter-projects.hooks';
import { usePromiseSnackbar } from '@client/hooks/use-snackbar.hooks';
import { useProjectsForOverview } from '@client/store/project';
import { matchesProjectProgramFilter } from '@client/hooks/use-filter-projects.helpers';
import {
  ActivityType,
  activityTypeColorRecord,
  programActivityTypes,
} from '@shared/models/design/activities/activity-type.model';
import { getActivityTypeLabel } from '@shared/helpers/design/activities.helpers';
import {
  loadProjectLabels,
  useProjectLabelFilter,
  useProjectLabels,
} from './labels/project-labels.hooks';
import { ProjectLabelFilterOptions } from './labels/ProjectLabels';

const PROGRAMS = [...programActivityTypes, ActivityType.Other];

/** Project filters share URL state with the list, overview and existing label chips. */
export const ProjectFiltersPanel = () => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [owner, setOwner] = useProjectOwnerFilter();
  const [programFilter, setProgramFilter] = useProjectProgramFilter();
  const [labelFilter] = useProjectLabelFilter();
  const selectedPrograms = programFilter.split(',').filter(Boolean);
  const { organization } = useProjectLabels();
  const projects = useProjectsForOverview();
  const promiseSnackbar = usePromiseSnackbar();
  const filterCount =
    new Set(selectedPrograms).size +
    new Set(labelFilter.split(',').filter(Boolean)).size +
    (owner === 'me' ? 1 : 0);
  const active = filterCount > 0;
  const filterLabel = active
    ? `View settings (${filterCount} active filters)`
    : 'View settings';

  useEffect(() => {
    if (!organization) return;
    const load = () => {
      void promiseSnackbar(() => loadProjectLabels(organization));
    };
    load();
    window.addEventListener('focus', load);
    return () => window.removeEventListener('focus', load);
  }, [organization, promiseSnackbar]);

  return (
    <>
      <Tooltip title={filterLabel} disableInteractive>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            flexShrink: 0,
            color: active ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.5)',
            '&:hover': { color: 'rgba(0,0,0,0.8)' },
          }}
        >
          <SelectTrigger
            aria-label={filterLabel}
            icon={<Icon category="ui" icon="filter" size={18} />}
            showCaret={false}
            suffix={
              active ? (
                <Box
                  component="span"
                  data-project-filter-count=""
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 15,
                    height: 15,
                    boxSizing: 'border-box',
                    borderRadius: '7.5px',
                    bgcolor: 'rgba(0,0,0,0.8)',
                    color: '#fff',
                    fontSize: 10,
                    lineHeight: 1,
                    letterSpacing: 0,
                    fontVariantNumeric: 'lining-nums tabular-nums',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      minWidth: 15,
                      height: 15,
                      px: '3px',
                      boxSizing: 'border-box',
                      lineHeight: '15px',
                      textAlign: 'center',
                    }}
                  >
                    {filterCount}
                  </Box>
                </Box>
              ) : undefined
            }
            open={!!anchor}
            onClick={(event) => setAnchor(event.currentTarget)}
          />
        </Box>
      </Tooltip>
      <Menu
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        width={320}
        searchable="Find labels or programs…"
      >
        <Menu.Group label="Calculation scope">
          <Box sx={{ px: 1.5, py: 1 }}>
            <PhasesCard panelAlignment="left" context="overview" portal />
          </Box>
        </Menu.Group>
        <Menu.Group label="Results">
          <Box sx={{ px: 1.5, py: 1 }}>
            <ResultCard nested />
          </Box>
        </Menu.Group>
        <Menu.Divider />
        <Menu.Group label="Projects">
          <Box sx={{ px: 1.5, py: 1 }}>
            <ProjectListFilter
              selectedTab={owner === 'me' ? 'my projects' : 'all projects'}
              onChange={(tab) => setOwner(tab === 'my projects' ? 'me' : '')}
            />
          </Box>
        </Menu.Group>
        <Menu.Divider />
        <Menu.Group label="Programs">
          {PROGRAMS.map((type) => {
            const label = getActivityTypeLabel(type);
            return (
              <Menu.CheckboxItem
                key={type}
                searchText={label}
                checked={selectedPrograms.includes(type)}
                onChange={() =>
                  setProgramFilter(
                    (selectedPrograms.includes(type)
                      ? selectedPrograms.filter((value) => value !== type)
                      : [...selectedPrograms, type]
                    ).join(','),
                  )
                }
                uncheckedIcon={
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: activityTypeColorRecord[type],
                    }}
                  />
                }
              >
                {label}
                <Menu.Item.RightIcon>
                  {String(
                    projects.filter((project) =>
                      matchesProjectProgramFilter(project, [type]),
                    ).length,
                  )}
                </Menu.Item.RightIcon>
              </Menu.CheckboxItem>
            );
          })}
        </Menu.Group>
        <Menu.Divider />
        <Menu.Group label="Labels">
          <ProjectLabelFilterOptions />
        </Menu.Group>
      </Menu>
    </>
  );
};
